import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaTimes, FaExclamationTriangle, FaExternalLinkAlt, FaDownload, FaCog } from "react-icons/fa";
import { modalBox, modalInput, modalLabel, modalPrimary } from "./FolgevertragModal";
import { API_BASE } from "../../api/config";
import FolgevertragModal from "./FolgevertragModal";
import NeuerMieterAssistent from "./NeuerMieterAssistent";
import FirmenwechselAssistent from "./schreiben/FirmenwechselAssistent";
import DokumentenPaket from "./DokumentenPaket";
import UebergabeprotokollForm from "./UebergabeprotokollForm";
import ThemenVerwaltung from "./schreiben/ThemenVerwaltung";
import Tab7Schreiben from "./schreiben/Tab7Schreiben";
import "./Tab6Vorgaenge.css";

const API = `${API_BASE}/mietverwaltung/api/kuendigung`;
const heute = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => {
  if (!d) return "–";
  const s = String(d).slice(0, 10);
  const p = s.split("-");
  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : s;
};
const fmtDT = (d) => {
  if (!d) return "–";
  const s = String(d).replace("T", " ");
  return `${fmtDate(s.slice(0, 10))}${s.length > 10 ? " " + s.slice(11, 16) : ""}`;
};

const STATUS_CLS = {
  erfasst: "vg-badge-gray", versendet: "vg-badge-blue", widerspruch: "vg-badge-amber",
  ausgezogen: "vg-badge-violet", abgeschlossen: "vg-badge-green", abgebrochen: "vg-badge-red",
  // Welle 106: Einzug
  vertrag_da: "vg-badge-blue", eingezogen: "vg-badge-violet",
};
const FLAG_TEXT = {
  einzug_offen: "Im Einzug fehlen noch Schritte",
  bestaetigung_offen: "Bestätigung an den Mieter steht seit über 10 Tagen aus",
  widerspruchsfrist_laeuft: "Widerspruchsfrist des Mieters läuft in den nächsten 14 Tagen ab",
  uebergabe_offen: "Vertragsende erreicht, Übergabe noch nicht erfasst",
  kaution_faellig: "Kautionsabrechnung ist fällig",
};
const PHASEN = [1, 2, 3, 4];
const DOK_TYP_LABEL = {
  kuendigung_vermieter: "Kündigungsschreiben (Vermieter)", bestaetigung_mieterkuendigung: "Kündigungsbestätigung",
  kuendigung_mieter: "Kündigung des Mieters", bestaetigung_mieter: "Bestätigung des Mieters", widerspruch: "Widerspruch",
  uebergabeprotokoll: "Übergabeprotokoll", mietbescheinigung: "Mietbescheinigung Sozialamt",
  mietbescheinigung_wohngeld: "Mietbescheinigung Wohngeld", wohnungsgeber: "Wohnungsgeberbescheinigung",
  sepa: "SEPA-Mandat", sonstiges: "Sonstiges", uebergabeprotokoll_vorlage: "Übergabeprotokoll (Vorlage)",
  kautionsabrechnung: "Kautionsabrechnung", mietvertrag: "Mietvertrag", selbstauskunft: "Selbstauskunft",
};
// Beim Einzug werden andere Papiere hochgeladen als bei der Kündigung
const UPLOAD_TYPEN = ["kuendigung_mieter", "bestaetigung_mieter", "widerspruch", "uebergabeprotokoll", "sonstiges"];
const UPLOAD_TYPEN_EINZUG = ["mietvertrag", "uebergabeprotokoll", "selbstauskunft", "wohnungsgeber", "mietbescheinigung_wohngeld", "sepa", "sonstiges"];

async function blobDownload(res, fallback) {
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m2 = cd.match(/filename\*=UTF-8''([^;]+)/);
  const m1 = cd.match(/filename="([^"]+)"/);
  const name = m2 ? decodeURIComponent(m2[1]) : (m1 ? m1[1] : fallback);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  return name;
}

function StatusBadge({ vorgang }) {
  return <span className={`vg-badge ${STATUS_CLS[vorgang.status] || "vg-badge-gray"}`}>{vorgang.status_label}</span>;
}

function phaseAktiv(v) {
  if (v.status === "abgeschlossen") return 5;
  if (v.art === "einzug") {
    // Welle 109: der Einzug kennt die Kündigungs-Status nicht — aktiv ist die erste
    // Phase mit offenem Pflichtschritt (vorher blieb der Stepper immer auf 1)
    const offen = (v.schritte || []).filter((s) => s.pflicht && !s.erledigt_am).map((s) => s.phase);
    return offen.length ? Math.min(...offen) : 4;
  }
  if (v.status === "ausgezogen") return 4;
  if (v.status === "versendet" || v.status === "widerspruch") {
    const offen3 = (v.schritte || []).some((s) => s.phase === 3 && s.pflicht && !s.erledigt_am);
    return offen3 ? 3 : 4;
  }
  return 1;
}

/* ────────────────────────────────────────────────────────────────────────── */
export default function Tab6Vorgaenge({ initialVorgang, initialFirmenwechsel = "", vollbild = false }) {
  const navigate = useNavigate();
  const [daten, setDaten] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [objekt, setObjekt] = useState("");
  const [art, setArt] = useState("");                          // Welle 106: Kündigung/Einzug
  const [nurOffen, setNurOffen] = useState(true);
  const [offenId, setOffenId] = useState(initialVorgang ? Number(initialVorgang) : null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [meldung, setMeldung] = useState("");
  const [fehler, setFehler] = useState("");
  const [busy, setBusy] = useState(false);
  const [abbruchGrund, setAbbruchGrund] = useState(null); // null = Feld zu
  const [abschlussNotiz, setAbschlussNotiz] = useState("");
  const [versand, setVersand] = useState({ datum: heute(), zustellart: "post" });
  const [folgevertrag, setFolgevertrag] = useState(null);
  const [schreibenModal, setSchreibenModal] = useState(null);   // {typ, titel, betreff, text, ablage, fehlend, busy}
  const [upload, setUpload] = useState({ typ: "sonstiges", datum: heute(), datei: null, notiz: "" });
  const [themenOffen, setThemenOffen] = useState(false);        // Welle 90.1: Themen/Bausteine + Ablage
  const [vertragBusy, setVertragBusy] = useState(false);        // Welle 106: Mietvertrag erzeugen
  const [neuerMieter, setNeuerMieter] = useState(false);        // Welle 109: Assistent „Neuer Mieter"
  // Welle 125: Assistent „Firmenwechsel" — Welle 132: per Link (?firmenwechsel=neu|<id>&vollbild=1) direkt im eigenen Fenster
  const [firmenwechsel, setFirmenwechsel] = useState(
    initialFirmenwechsel ? (/^\d+$/.test(initialFirmenwechsel) ? Number(initialFirmenwechsel) : true) : false);
  const [schreibenStand, setSchreibenStand] = useState(0);      // Liste „Weitere Vorgänge" neu laden
  const [protokoll, setProtokoll] = useState(null);             // Welle 109: {art} — Übergabeprotokoll-Formular

  const mietvertragErzeugen = async (nr) => {
    setVertragBusy(true); setFehler(""); setMeldung("");
    try {
      const res = await fetch(`${API_BASE}/mietverwaltung/api/vertrag/${encodeURIComponent(nr)}/mietvertrag?ablegen=1`,
        { method: "POST" });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Mietvertrag konnte nicht erzeugt werden");
      }
      const url = URL.createObjectURL(await res.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      const pfad = res.headers.get("X-Ablage-Pfad");
      setMeldung(pfad ? `Mietvertrag abgelegt: ${decodeURIComponent(pfad)}` : "Mietvertrag erzeugt.");
      ladeDetail(detail?.id);
    } catch (e) { setFehler(String(e.message || e)); }
    setVertragBusy(false);
  };

  // ── Welle 82: Schreiben / Dokumente ──────────────────────────────────
  const oeffneSchreiben = async (typ) => {
    if (!detail) return;
    setFehler("");
    try {
      const res = await fetch(`${API}/${detail.id}/schreiben/vorschau?typ=${typ}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Vorschau fehlgeschlagen");
      setSchreibenModal({ typ, titel: d.titel, betreff: d.betreff, text: d.text_gerendert, roh: d.text_roh,
        ablage: d.ablage, fehlend: d.platzhalter_fehlend || [], absender: d.absender, empfaenger: d.empfaenger, busy: false });
    } catch (e) { setFehler(String(e.message || e)); }
  };
  const schreibenPdf = async (nurVorschau) => {
    if (!schreibenModal || !detail) return;
    setSchreibenModal((m) => ({ ...m, busy: true }));
    try {
      const res = await fetch(`${API}/${detail.id}/schreiben?typ=${schreibenModal.typ}&nur_vorschau=${nurVorschau ? 1 : 0}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text_override: schreibenModal.text }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "PDF fehlgeschlagen");
      const name = await blobDownload(res, `${schreibenModal.titel} - ${detail.mietvertrag_nr}.pdf`);
      if (!nurVorschau) {
        const pfad = res.headers.get("X-Ablage-Pfad");
        const quelle = res.headers.get("X-Ablage-Quelle");
        setMeldung(pfad ? `„${name}“ erzeugt und abgelegt: ${decodeURIComponent(pfad)}${quelle === "fallback" ? " (Sammelordner)" : ""}`
          : `„${name}“ erzeugt (keine Dropbox-Ablage: ${quelle}).`);
        setSchreibenModal(null);
        ladeDetail(detail.id);
      } else {
        setSchreibenModal((m) => ({ ...m, busy: false }));
      }
    } catch (e) { setFehler(String(e.message || e)); setSchreibenModal((m) => (m ? { ...m, busy: false } : m)); }
  };
  const dokumentDownload = async (d) => {
    try {
      const res = await fetch(`${API}/dokument/${d.id}/download`);
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Download fehlgeschlagen");
      await blobDownload(res, d.dateiname || "dokument.pdf");
    } catch (e) { setFehler(String(e.message || e)); }
  };
  const dokumentVersand = async (d, datum) => {
    setBusy(true); setFehler("");
    try {
      const res = await fetch(`${API}/dokument/${d.id}`, { method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ versendet_am: datum, zustellart: versand.zustellart }) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || "Speichern fehlgeschlagen");
      setDetail(j); laden();
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };
  const uploadSenden = async () => {
    if (!detail || !upload.datei) { setFehler("Bitte eine Datei wählen."); return; }
    setBusy(true); setFehler("");
    try {
      const fd = new FormData();
      fd.append("datei", upload.datei);
      fd.append("typ", upload.typ);
      if (upload.datum) fd.append("eingegangen_am", upload.datum);
      if (upload.notiz) fd.append("notiz", upload.notiz);
      const res = await fetch(`${API}/${detail.id}/upload`, { method: "POST", body: fd });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || "Upload fehlgeschlagen");
      setDetail(j); laden();
      setUpload({ typ: "sonstiges", datum: heute(), datei: null, notiz: "" });
      setMeldung(j.ruecklauf
        ? `Am QR-Code erkannt: unterschriebener Rücklauf — abgelegt als „${j.ruecklauf.dateiname}“`
          + (j.ruecklauf.schritte?.length ? `, abgehakt: ${j.ruecklauf.schritte.join(", ")}` : "")
        : "Dokument hochgeladen und im Vertragsordner abgelegt.");
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };
  const laden = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}?offen=0`);
      const d = await res.json();
      setDaten(d);
    } catch (e) { setFehler(String(e.message || e)); }
    setLoading(false);
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const ladeDetail = useCallback(async (id, still = false) => {
    if (!id) { setDetail(null); return; }
    // still: Aktualisieren im offenen Vorgang ohne Ladekreisel — sonst baut sich die
    // Schublade neu auf und z. B. die Auswahl im Dokumentenpaket geht verloren
    if (!still) setDetailLoading(true);
    try {
      const res = await fetch(`${API}/${id}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Vorgang nicht gefunden");
      setDetail(d);
      setVersand({ datum: heute(), zustellart: d.zustellart || (d.richtung === "vermieter" ? "einwurf_einschreiben" : "post") });
    } catch (e) { setFehler(String(e.message || e)); setDetail(null); }
    setDetailLoading(false);
  }, []);
  useEffect(() => { ladeDetail(offenId); }, [offenId, ladeDetail]);

  const api = async (pfad, method, body) => {
    setBusy(true); setFehler(""); setMeldung("");
    try {
      const res = await fetch(`${API}${pfad}`, { method, headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : undefined });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || `Fehler ${res.status}`);
      setDetail(d);
      laden();
      return d;
    } catch (e) { setFehler(String(e.message || e)); return null; }
    finally { setBusy(false); }
  };

  const feldSpeichern = (feld, wert) => detail && api(`/${detail.id}`, "PUT", { felder: { [feld]: wert } });
  const schrittSpeichern = (key, patch) => {
    if (!detail) return;
    const s = (detail.schritte || []).find((x) => x.schritt_key === key) || {};
    const body = { erledigt_am: s.erledigt_am || null, wert: s.wert || null, notiz: s.notiz || null, ...patch };
    return api(`/${detail.id}/schritt/${key}`, "PUT", body);
  };

  const zeilen = useMemo(() => {
    if (!daten?.vorgaenge) return [];
    return daten.vorgaenge.filter((v) =>
      (!filter || v.status === filter) &&
      (!objekt || v.objekt_id === objekt) &&
      (!art || (v.art || "kuendigung") === art) &&
      (!nurOffen || !["abgeschlossen", "abgebrochen"].includes(v.status)));
  }, [daten, filter, objekt, art, nurOffen]);
  const objekte = useMemo(() => Array.from(new Set((daten?.vorgaenge || []).map((v) => v.objekt_id).filter(Boolean))).sort(), [daten]);
  const offenGesamt = (daten?.vorgaenge || []).filter((v) => !["abgeschlossen", "abgebrochen"].includes(v.status)).length;

  if (loading && !daten) {
    return <div className="me-loading"><div className="me-spinner" /><p>Vorgänge werden geladen…</p></div>;
  }

  const schritteNachPhase = (v) => PHASEN.map((p) => ({
    phase: p, label: v.phasen?.[p] || `Phase ${p}`, schritte: (v.schritte || []).filter((s) => s.phase === p),
  })).filter((g) => g.schritte.length);

  const linkFuer = (s, v) => {
    if (s.link === "zaehler") return () => navigate("/zaehlerstand");
    if (s.link === "wohnungsgeber") return () => navigate(`/mietverwaltung?tab=bescheinigungen&doc=wohnungsgeber&vertrag=${v.mietvertrag_nr}`);
    if (s.link === "folgevertrag") return () => setFolgevertrag({ einheit: v.einheit });
    return null;
  };

  return (
    <>
      <div className="vg-kopf">
        <div className="vg-chips">
          <span className={`vg-chip ${filter === "" ? "aktiv" : ""}`} onClick={() => setFilter("")}>
            Alle ({nurOffen ? offenGesamt : (daten?.vorgaenge || []).length})
          </span>
          {(daten?.status_reihenfolge || []).map((code) => (
            <span key={code} className={`vg-chip ${filter === code ? "aktiv" : ""}`}
              onClick={() => setFilter(filter === code ? "" : code)}>
              {daten.status_namen[code]} ({daten.zaehler[code] ?? 0})
            </span>
          ))}
        </div>
        <div className="vg-filter">
          {/* Welle 106: Kündigung und Einzug laufen in derselben Liste */}
          <select className="me-select me-select-sm" value={art} onChange={(e) => setArt(e.target.value)}>
            <option value="">Alle Arten</option>
            <option value="kuendigung">nur Kündigungen</option>
            <option value="einzug">nur Einzüge</option>
          </select>
          <select className="me-select me-select-sm" value={objekt} onChange={(e) => setObjekt(e.target.value)}>
            <option value="">Alle Objekte</option>
            {objekte.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <label className="vg-check"><input type="checkbox" checked={nurOffen} onChange={(e) => setNurOffen(e.target.checked)} /> nur offene</label>
          <button className="me-btn me-btn-primary" onClick={() => setNeuerMieter(true)}
            title="Neuen Mieter anlegen: Einzug + Auszug des bisherigen Mieters, verknüpft">➕ Neuer Mieter</button>
          <button className="me-btn me-btn-primary" onClick={() => setFirmenwechsel(true)}
            title="Alle Vertragspartner informieren, dass ihre Verträge auf eine andere Firma übergehen (z. B. FBG → FVV)">🔁 Firmenwechsel</button>
          <button className="me-btn me-btn-outline" onClick={() => setThemenOffen(true)} title="Themen, Textbausteine und Ablage-Regel bearbeiten"><FaCog /> Textbausteine &amp; Ablage</button>
        </div>
      </div>

      {fehler && <div className="vg-fehler">{fehler}</div>}
      {meldung && <div className="vg-meldung">{meldung}</div>}

      <div className="me-table-section">
        <div className="me-table-header"><h3>
          {art === "einzug" ? "Einzüge" : art === "kuendigung" ? "Kündigungsvorgänge" : "Vorgänge"} ({zeilen.length})
        </h3></div>
        <div className="me-table-wrap">
          <table className="me-table vg-table">
            <thead>
              <tr>
                <th>Einheit / MV-Nr</th><th>Mieter</th><th>Art</th><th>Status</th>
                <th>Zugang</th><th>Kündigung zum</th><th>Nächste Frist</th><th>Pflicht</th><th></th>
              </tr>
            </thead>
            <tbody>
              {zeilen.length === 0 && (
                <tr><td colSpan={9} className="vg-leer">Keine Vorgänge. Eine Kündigung startest du im Tab „Objekte &amp; Verträge“ über „Kündigen“; ein Einzug entsteht automatisch, sobald ein neuer Mietvertrag angelegt wird.</td></tr>
              )}
              {zeilen.map((v) => (
                <tr key={v.id} className={`vg-zeile ${offenId === v.id ? "aktiv" : ""}`} onClick={() => setOffenId(v.id)}>
                  <td><b>{v.einheit}</b><br /><span className="vg-mono">{v.mietvertrag_nr}</span></td>
                  <td>{v.mieter || v.mieter1_name || "–"}</td>
                  <td>{v.art === "einzug" ? "Einzug"
                    : v.richtung === "vermieter" ? "Vermieter kündigt" : "Mieter kündigt"}</td>
                  <td><StatusBadge vorgang={v} /></td>
                  <td>{fmtDate(v.eingang_am || v.schreiben_datum)}</td>
                  <td>{fmtDate(v.kuendigung_zum)}</td>
                  <td>
                    {v.naechste_frist ? (
                      <span className={`vg-frist ${v.naechste_frist.tage < 0 ? "ueber" : v.naechste_frist.tage <= 14 ? "bald" : ""}`}>
                        {v.naechste_frist.label} {fmtDate(v.naechste_frist.datum)}
                        <small>{v.naechste_frist.tage < 0 ? ` (${-v.naechste_frist.tage} T. überfällig)` : ` (in ${v.naechste_frist.tage} T.)`}</small>
                      </span>
                    ) : "–"}
                  </td>
                  <td className="num">{v.pflicht_gesamt - v.pflicht_offen}/{v.pflicht_gesamt}</td>
                  <td>{(v.ueberfaellig || []).length > 0 && (
                    <span className="vg-warn" title={v.ueberfaellig.map((f) => FLAG_TEXT[f] || f).join("\n")}>
                      <FaExclamationTriangle /> {v.ueberfaellig.length}
                    </span>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Weitere Vorgänge (Welle 90.1): Umzug, SEPA-Mandat, … — modulare Schreiben-Vorgänge ── */}
      <div className="me-table-section" style={{ marginTop: 18 }}>
        <Tab7Schreiben key={schreibenStand} eingebettet onProzess={(p, vorgangId) => {
          if (p === "neuer_mieter") setNeuerMieter(true);
          if (p === "firmenwechsel") setFirmenwechsel(vorgangId || true);   // bestehender Vorgang → wieder im Assistenten
        }} />
      </div>

      {/* ── Detail-Drawer ── */}
      {offenId && (
        <div className="me-drawer-overlay" onClick={() => setOffenId(null)}>
          <div className="me-drawer vg-drawer" onClick={(e) => e.stopPropagation()}>
            {detailLoading || !detail ? (
              <div className="me-loading"><div className="me-spinner" /><p>Lade Vorgang…</p></div>
            ) : (
              <>
                <div className="me-drawer-header">
                  <div>
                    <h3>{detail.vertrag?.mv_kurzname || detail.mietvertrag_nr} <StatusBadge vorgang={detail} /></h3>
                    <span className="me-drawer-sub">
                      {detail.mietvertrag_nr} · Objekt {detail.objekt_id} · Einheit {detail.einheit} ·{" "}
                      {detail.art === "einzug" ? "Einzug"
                        : detail.richtung === "vermieter" ? "Vermieter kündigt" : "Mieter kündigt"}
                    </span>
                  </div>
                  <button className="me-btn me-btn-icon" onClick={() => setOffenId(null)}><FaTimes /></button>
                </div>

                <div className="me-drawer-body">
                  {/* Stepper */}
                  <div className="vg-stepper">
                    {PHASEN.map((p) => {
                      const akt = phaseAktiv(detail);
                      return (
                        <div key={p} className={`vg-step ${akt === p ? "aktiv" : akt > p ? "fertig" : ""}`}>
                          <span className="vg-step-nr">{p}</span><span>{detail.phasen?.[p]}</span>
                        </div>
                      );
                    })}
                  </div>

                  {(detail.ueberfaellig || []).length > 0 && (
                    <div className="vg-warnbox">
                      {detail.ueberfaellig.map((f) => <div key={f}><FaExclamationTriangle /> {FLAG_TEXT[f] || f}</div>)}
                    </div>
                  )}

                  {/* Kopfdaten — Welle 106: der Einzug hat eigene Felder */}
                  {detail.art === "einzug" ? (
                    <div className="me-drawer-section">
                      <h4>Einzug</h4>
                      <div className="vg-grid">
                        <span>Vertragsbeginn</span>
                        <input type="date" className="vg-in" value={detail.schreiben_datum || ""} disabled={busy}
                          onChange={(e) => feldSpeichern("schreiben_datum", e.target.value || null)} />
                        <span>Vertrag unterschrieben am</span>
                        <input type="date" className="vg-in" value={detail.schreiben_versendet_am || ""} disabled={busy}
                          onChange={(e) => feldSpeichern("schreiben_versendet_am", e.target.value || null)} />
                        <span>Übergabetermin</span>
                        <input type="datetime-local" className="vg-in"
                          value={(detail.uebergabe_termin || "").replace(" ", "T").slice(0, 16)} disabled={busy}
                          onChange={(e) => feldSpeichern("uebergabe_termin", e.target.value || null)} />
                        <span>Eingezogen am</span>
                        <input type="date" className="vg-in" value={detail.uebergabe_am || ""} disabled={busy}
                          onChange={(e) => feldSpeichern("uebergabe_am", e.target.value || null)} />
                        <span>Kaution</span>
                        <span>{detail.vertrag?.kaution
                          ? `${Number(detail.vertrag.kaution).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €`
                          : "–"}</span>
                        <span>Notiz</span>
                        <textarea className="vg-in vg-ta" defaultValue={detail.notiz || ""}
                          onBlur={(e) => e.target.value !== (detail.notiz || "") && feldSpeichern("notiz", e.target.value)} />
                      </div>
                    </div>
                  ) : (
                  <div className="me-drawer-section">
                    <h4>Kündigung</h4>
                    <div className="vg-grid">
                      <span>{detail.richtung === "vermieter" ? "Schreiben vom" : "Schreiben des Mieters"}</span>
                      <input type="date" className="vg-in" value={detail.schreiben_datum || ""} disabled={busy || detail.status === "abgeschlossen" || detail.status === "abgebrochen"}
                        onChange={(e) => feldSpeichern("schreiben_datum", e.target.value || null)} />
                      <span>Zugang am</span>
                      <input type="date" className="vg-in" value={detail.eingang_am || ""} disabled={busy}
                        onChange={(e) => feldSpeichern("eingang_am", e.target.value || null)} />
                      <span>Kündigung zum</span>
                      <div className="vg-inline">
                        <input type="date" className="vg-in" value={detail.kuendigung_zum || ""} disabled={busy}
                          onChange={(e) => feldSpeichern("kuendigung_zum", e.target.value || null)} />
                        <small>Regel: {detail.frist_regel || "–"}{detail.fristen?.kuendigung_zum_berechnet && detail.fristen.kuendigung_zum_berechnet !== detail.kuendigung_zum ? ` · berechnet ${fmtDate(detail.fristen.kuendigung_zum_berechnet)}` : ""}</small>
                      </div>
                      {detail.richtung === "vermieter" && (
                        <>
                          <span>Widerspruch bis</span><span>{fmtDate(detail.widerspruch_frist)} <small>(§ 574b, 2 Monate vor Ende)</small></span>
                          <span>Grund</span>
                          <div className="vg-inline">
                            <select className="vg-in" value={detail.grund_key || ""} disabled={busy}
                              onChange={(e) => feldSpeichern("grund_key", e.target.value || null)}>
                              <option value="">–</option>
                              {Object.entries(detail.gruende || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                            </select>
                            <textarea className="vg-in vg-ta" defaultValue={detail.grund_text || ""} placeholder="Begründung"
                              onBlur={(e) => e.target.value !== (detail.grund_text || "") && feldSpeichern("grund_text", e.target.value)} />
                          </div>
                          <span>Widerspruch am</span>
                          <div className="vg-inline">
                            <input type="date" className="vg-in" value={detail.widerspruch_am || ""} disabled={busy}
                              onChange={(e) => feldSpeichern("widerspruch_am", e.target.value || null)} />
                            {detail.widerspruch_am && (
                              <select className="vg-in" value={detail.widerspruch_ergebnis || ""} disabled={busy}
                                onChange={(e) => feldSpeichern("widerspruch_ergebnis", e.target.value || null)}>
                                <option value="">Ergebnis offen</option>
                                <option value="abgelehnt">Widerspruch abgelehnt</option>
                                <option value="fortsetzung">Fortsetzung des Mietverhältnisses</option>
                                <option value="vergleich">Vergleich</option>
                              </select>
                            )}
                          </div>
                        </>
                      )}
                      <span>Versendet am</span>
                      <div className="vg-inline">
                        <input type="date" className="vg-in" value={detail.schreiben_versendet_am || ""} disabled={busy}
                          onChange={(e) => feldSpeichern("schreiben_versendet_am", e.target.value || null)} />
                        <select className="vg-in" value={detail.zustellart || ""} disabled={busy}
                          onChange={(e) => feldSpeichern("zustellart", e.target.value || null)}>
                          <option value="">Zustellart</option>
                          {(detail.zustellarten || []).map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
                        </select>
                      </div>
                      <span>Übergabetermin</span>
                      <input type="datetime-local" className="vg-in" value={(detail.uebergabe_termin || "").replace(" ", "T").slice(0, 16)} disabled={busy}
                        onChange={(e) => feldSpeichern("uebergabe_termin", e.target.value || null)} />
                      <span>Ausgezogen am</span>
                      <input type="date" className="vg-in" value={detail.uebergabe_am || ""} disabled={busy}
                        onChange={(e) => feldSpeichern("uebergabe_am", e.target.value || null)} />
                      <span>Kaution fällig</span>
                      <span>{fmtDate(detail.kaution_faellig_am)} {detail.vertrag?.kaution ? <small>({Number(detail.vertrag.kaution).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €)</small> : null}</span>
                      <span>Notiz</span>
                      <textarea className="vg-in vg-ta" defaultValue={detail.notiz || ""}
                        onBlur={(e) => e.target.value !== (detail.notiz || "") && feldSpeichern("notiz", e.target.value)} />
                    </div>
                  </div>
                  )}

                  {/* Aktionskarte je Status */}
                  {!["abgeschlossen", "abgebrochen"].includes(detail.status) && (
                    <div className="me-drawer-section vg-aktion">
                      <h4>Nächster Schritt</h4>
                      {detail.art === "einzug" && detail.status === "erfasst" && (
                        <div className="vg-inline">
                          <span>Vertrag unterschrieben am</span>
                          <input type="date" className="vg-in" value={versand.datum}
                            onChange={(e) => setVersand({ ...versand, datum: e.target.value })} />
                          <button className="me-btn me-btn-primary" disabled={busy}
                            onClick={() => api(`/${detail.id}`, "PUT", { felder: { schreiben_versendet_am: versand.datum } })}>
                            Vertrag ist da
                          </button>
                        </div>
                      )}
                      {detail.art === "einzug" && detail.status === "vertrag_da" && (
                        <div className="vg-inline">
                          <span>Eingezogen am</span>
                          <input type="date" className="vg-in" value={versand.datum}
                            onChange={(e) => setVersand({ ...versand, datum: e.target.value })} />
                          <button className="me-btn me-btn-primary" disabled={busy}
                            onClick={() => api(`/${detail.id}`, "PUT", { felder: { uebergabe_am: versand.datum } })}>
                            Einzug eintragen
                          </button>
                        </div>
                      )}
                      {detail.art !== "einzug" && detail.status === "erfasst" && (
                        <div className="vg-inline">
                          <span>{detail.richtung === "vermieter" ? "Kündigung zugestellt am" : "Bestätigung versendet am"}</span>
                          <input type="date" className="vg-in" value={versand.datum} onChange={(e) => setVersand({ ...versand, datum: e.target.value })} />
                          <select className="vg-in" value={versand.zustellart} onChange={(e) => setVersand({ ...versand, zustellart: e.target.value })}>
                            {(detail.zustellarten || []).map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
                          </select>
                          <button className="me-btn me-btn-primary" disabled={busy}
                            onClick={() => api(`/${detail.id}`, "PUT", { felder: { schreiben_versendet_am: versand.datum, zustellart: versand.zustellart } })}>
                            Versand eintragen
                          </button>
                        </div>
                      )}
                      {(detail.status === "versendet" || detail.status === "widerspruch") && (
                        <div className="vg-inline">
                          {detail.status === "widerspruch" && <span>Widerspruch liegt vor — Ergebnis oben eintragen, dann:</span>}
                          <button className="me-btn me-btn-primary" disabled={busy}
                            onClick={() => feldSpeichern("uebergabe_am", detail.kuendigung_zum || heute())}>
                            Auszug erfassen ({fmtDate(detail.kuendigung_zum || heute())})
                          </button>
                          <button className="me-btn me-btn-outline" disabled={busy} onClick={() => setFolgevertrag({ einheit: detail.einheit })}>Folgevertrag anlegen</button>
                        </div>
                      )}
                      {(detail.status === "ausgezogen" || (detail.art === "einzug" && detail.status === "eingezogen")) && (
                        <div className="vg-inline">
                          <input className="vg-in" placeholder="Notiz zum Abschluss (optional)" value={abschlussNotiz} onChange={(e) => setAbschlussNotiz(e.target.value)} />
                          <button className="me-btn me-btn-primary" disabled={busy}
                            onClick={async () => {
                              const d = await api(`/${detail.id}/abschliessen`, "POST", { notiz: abschlussNotiz });
                              if (!d) setMeldung("Offene Pflichtschritte — unten abhaken oder „Trotzdem abschließen“.");
                            }}>Abschließen</button>
                          <button className="me-btn me-btn-outline" disabled={busy}
                            onClick={() => api(`/${detail.id}/abschliessen`, "POST", { force: true, notiz: abschlussNotiz })}>Trotzdem abschließen</button>
                          <button className="me-btn me-btn-outline" disabled={busy} onClick={() => setFolgevertrag({ einheit: detail.einheit })}>Folgevertrag anlegen</button>
                        </div>
                      )}
                      <div className="vg-inline vg-abbruch">
                        {abbruchGrund === null ? (
                          <button className="me-btn me-btn-danger" disabled={busy} onClick={() => setAbbruchGrund("")}>Vorgang abbrechen</button>
                        ) : (
                          <>
                            <input className="vg-in" placeholder="Grund (z. B. Kündigung zurückgenommen)" value={abbruchGrund} onChange={(e) => setAbbruchGrund(e.target.value)} />
                            <button className="me-btn me-btn-danger" disabled={busy}
                              onClick={async () => { const d = await api(`/${detail.id}/abbrechen`, "POST", { grund: abbruchGrund }); if (d) setAbbruchGrund(null); }}>
                              Abbrechen bestätigen
                            </button>
                            <button className="me-btn me-btn-outline" onClick={() => setAbbruchGrund(null)}>Doch nicht</button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  {detail.status === "abgebrochen" && (
                    <div className="vg-warnbox">Abgebrochen am {fmtDate(detail.abgebrochen_am)}{detail.abbruch_grund ? ` — ${detail.abbruch_grund}` : ""}. Die Vertragsfelder wurden wiederhergestellt.</div>
                  )}
                  {detail.status === "abgeschlossen" && (
                    <div className="vg-okbox">Abgeschlossen am {fmtDate(detail.abgeschlossen_am)}. Vertragsende {fmtDate(detail.vertrag?.vertrag_ende)}, Auszug {fmtDate(detail.vertrag?.auszug)}.</div>
                  )}

                  {/* Schrittliste */}
                  {schritteNachPhase(detail).map((g) => (
                    <div className="me-drawer-section" key={g.phase}>
                      <h4>{g.phase}. {g.label}</h4>
                      <div className="vg-schritte">
                        {g.schritte.map((s) => {
                          const link = linkFuer(s, detail);
                          return (
                            <div key={s.schritt_key} className={`vg-schritt ${s.erledigt_am ? "erledigt" : ""} ${s.pflicht ? "pflicht" : ""}`}>
                              <label className="vg-schritt-kopf">
                                <input type="checkbox" checked={!!s.erledigt_am} disabled={busy}
                                  onChange={(e) => schrittSpeichern(s.schritt_key, { erledigt_am: e.target.checked ? heute() : null })} />
                                <span className="vg-schritt-label">{s.label}{s.pflicht ? <b title="Pflichtschritt"> *</b> : null}</span>
                                {link && <button type="button" className="vg-link" onClick={link}><FaExternalLinkAlt /> öffnen</button>}
                              </label>
                              <div className="vg-schritt-felder">
                                <input type="date" className="vg-in" value={s.erledigt_am || ""} disabled={busy}
                                  onChange={(e) => schrittSpeichern(s.schritt_key, { erledigt_am: e.target.value || null })} />
                                <input className="vg-in" placeholder="Wert (z. B. Anzahl, Betrag, Nr.)" defaultValue={s.wert || ""}
                                  onBlur={(e) => e.target.value !== (s.wert || "") && schrittSpeichern(s.schritt_key, { wert: e.target.value })} />
                                <input className="vg-in vg-in-notiz" placeholder="Notiz" defaultValue={s.notiz || ""}
                                  onBlur={(e) => e.target.value !== (s.notiz || "") && schrittSpeichern(s.schritt_key, { notiz: e.target.value })} />
                              </div>
                              {s.hinweis && <small className="vg-hinweis">{s.hinweis}</small>}
                              {s.schritt_key === "sepa_beendet" && detail.vertrag?.mieter_iban && (
                                <small className="vg-hinweis">IBAN Mieter: {detail.vertrag.mieter_iban}</small>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Welle 109: Dokumentenpaket — Soll-Dokumente mit erzeugt/versandt/zurück */}
                  <div className="me-drawer-section">
                    <h4>Dokumentenpaket</h4>
                    <DokumentenPaket vorgang={detail}
                      onSchreiben={(typ) => oeffneSchreiben(typ)}
                      onProtokoll={() => setProtokoll({ art: detail.art === "einzug" ? "einzug" : "auszug" })}
                      onOeffneVorgang={(id) => setOffenId(id)}
                      onGeaendert={() => { ladeDetail(detail.id, true); laden(); }}
                      meldung={setMeldung} fehler={setFehler} />
                  </div>

                  {/* Dokumente (Welle 82) */}
                  <div className="me-drawer-section">
                    <h4>Dokumente ({(detail.dokumente || []).length})</h4>
                    {!["abgeschlossen", "abgebrochen"].includes(detail.status) && (
                      <div className="vg-inline" style={{ marginBottom: 8 }}>
                        {detail.art === "einzug" ? (
                          <>
                            {/* Welle 106: beim Einzug zählen andere Dokumente */}
                            <button className="me-btn me-btn-primary" disabled={busy || vertragBusy}
                              onClick={() => mietvertragErzeugen(detail.mietvertrag_nr)}>
                              {vertragBusy ? "Erzeuge…" : "Mietvertrag erzeugen"}
                            </button>
                            <button className="me-btn me-btn-outline" disabled={busy}
                              onClick={() => setProtokoll({ art: "einzug" })}>Übergabeprotokoll</button>
                            <button className="me-btn me-btn-outline"
                              onClick={() => navigate(`/mietverwaltung?tab=bescheinigungen&doc=wohnungsgeber&vertrag=${detail.mietvertrag_nr}`)}>
                              Wohnungsgeberbescheinigung
                            </button>
                          </>
                        ) : detail.richtung === "vermieter" ? (
                          <button className="me-btn me-btn-primary" disabled={busy} onClick={() => oeffneSchreiben("kuendigung")}>Kündigungsschreiben erzeugen</button>
                        ) : (
                          <button className="me-btn me-btn-primary" disabled={busy} onClick={() => oeffneSchreiben("bestaetigung")}>Kündigungsbestätigung erzeugen</button>
                        )}
                        {detail.art !== "einzug" && (
                          <>
                            <button className="me-btn me-btn-outline" disabled={busy} onClick={() => setProtokoll({ art: "auszug" })}>Übergabeprotokoll</button>
                            <button className="me-btn me-btn-outline" disabled={busy} onClick={() => oeffneSchreiben("kautionsabrechnung")}>Kautionsabrechnung</button>
                          </>
                        )}
                      </div>
                    )}
                    {(detail.dokumente || []).length === 0 ? (
                      <p className="vg-leer-text">Noch keine Dokumente.</p>
                    ) : (
                      <div className="vg-doks">
                        {detail.dokumente.map((d) => (
                          <div key={d.id} className="vg-dok">
                            <div className="vg-dok-kopf">
                              <span className={`vg-badge ${d.richtung === "ausgehend" ? "vg-badge-blue" : "vg-badge-violet"}`}>{d.richtung === "ausgehend" ? "ausgehend" : "eingehend"}</span>
                              <b>{DOK_TYP_LABEL[d.typ] || d.typ}</b>
                              <small>{fmtDT(d.erzeugt_am)}</small>
                              {d.dropbox_pfad
                                ? <button type="button" className="vg-link" onClick={() => dokumentDownload(d)}><FaDownload /> herunterladen</button>
                                : <small>(nicht in Dropbox)</small>}
                            </div>
                            <div className="vg-dok-name" title={d.dropbox_pfad || ""}>{d.dateiname}</div>
                            {d.richtung === "ausgehend" && (
                              d.versendet_am ? <small>versendet am {fmtDate(d.versendet_am)}</small> : (
                                <div className="vg-inline">
                                  <small>Versendet am</small>
                                  <input type="date" className="vg-in" value={versand.datum} onChange={(e) => setVersand({ ...versand, datum: e.target.value })} />
                                  <select className="vg-in" value={versand.zustellart} onChange={(e) => setVersand({ ...versand, zustellart: e.target.value })}>
                                    {(detail.zustellarten || []).map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
                                  </select>
                                  <button className="me-btn me-btn-outline" disabled={busy} onClick={() => dokumentVersand(d, versand.datum)}>Versand eintragen</button>
                                </div>
                              )
                            )}
                            {d.richtung === "eingehend" && d.eingegangen_am && <small>eingegangen am {fmtDate(d.eingegangen_am)}{d.notiz ? ` — ${d.notiz}` : ""}</small>}
                          </div>
                        ))}
                      </div>
                    )}
                    {!["abgeschlossen", "abgebrochen"].includes(detail.status) && (
                      <div className="vg-upload">
                        <b>Dokument hochladen</b> <small>(landet im Vertragsordner in Dropbox)</small>
                        <div className="vg-inline">
                          <select className="vg-in" value={upload.typ} onChange={(e) => setUpload({ ...upload, typ: e.target.value })}>
                            {(detail.art === "einzug" ? UPLOAD_TYPEN_EINZUG : UPLOAD_TYPEN).map((t) => <option key={t} value={t}>{DOK_TYP_LABEL[t]}</option>)}
                          </select>
                          <input type="date" className="vg-in" value={upload.datum} onChange={(e) => setUpload({ ...upload, datum: e.target.value })} />
                          <input type="file" className="vg-in vg-file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUpload({ ...upload, datei: e.target.files?.[0] || null })} />
                          <input className="vg-in" placeholder="Notiz" value={upload.notiz} onChange={(e) => setUpload({ ...upload, notiz: e.target.value })} />
                          <button className="me-btn me-btn-outline" disabled={busy || !upload.datei} onClick={uploadSenden}>Hochladen</button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="me-drawer-section vg-vertragslink">
                    <button className="me-btn me-btn-outline" onClick={() => navigate(`/mietverwaltung?tab=vertraege`)}>Zum Vertrag (Objekte &amp; Verträge)</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {schreibenModal && (
        <div className="me-drawer-overlay" style={{ display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setSchreibenModal(null)}>
          <div style={{ ...modalBox, width: "min(820px, 96vw)" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0 }}>{schreibenModal.titel} — {detail?.mietvertrag_nr}</h3>
            <p style={{ fontSize: 13, color: "#94a3b8", margin: "8px 0 0" }}>
              Text prüfen und bei Bedarf anpassen. <b>Fett</b> mit **…**, Aufzählung mit „- “. Platzhalter wie {"{kuendigung_zum}"} werden beim Erzeugen gefüllt.
              {detail?.richtung === "vermieter" && " Die Kündigung muss eigenhändig unterschrieben und schriftlich zugestellt werden (§ 568 BGB)."}
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10, fontSize: 12.5, color: "#cbd5e1" }}>
              <div><b>Absender:</b> {schreibenModal.absender?.name}, {schreibenModal.absender?.anschrift}</div>
              <div><b>Empfänger:</b> {(schreibenModal.empfaenger || []).join(", ")}</div>
            </div>
            <label style={{ ...modalLabel, marginTop: 10 }}>Betreff
              <input style={modalInput} value={schreibenModal.betreff} readOnly />
            </label>
            <label style={{ ...modalLabel, marginTop: 10 }}>Text
              <textarea style={{ ...modalInput, height: 320, padding: 10, resize: "vertical", fontFamily: "inherit", lineHeight: 1.4 }}
                value={schreibenModal.text} onChange={(e) => setSchreibenModal({ ...schreibenModal, text: e.target.value })} />
            </label>
            {schreibenModal.fehlend?.length > 0 && (
              <p style={{ fontSize: 12.5, color: "#fcd34d", margin: "8px 0 0" }}>Fehlende Angaben: {schreibenModal.fehlend.join(", ")} — bitte im Text ersetzen oder Stammdaten pflegen.</p>
            )}
            <p style={{ fontSize: 12.5, color: "#94a3b8", margin: "8px 0 0" }}>
              Ablage: {schreibenModal.ablage?.pfad || "keine Dropbox"}{schreibenModal.ablage?.quelle === "fallback" ? ` (Sammelordner — ${schreibenModal.ablage.grund})` : ""}
            </p>
            {fehler && <p style={{ color: "#fca5a5", fontSize: 13 }}>{fehler}</p>}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
              <button className="me-btn me-btn-outline" onClick={() => setSchreibenModal(null)}>Schließen</button>
              <button className="me-btn me-btn-outline" disabled={schreibenModal.busy} onClick={() => schreibenPdf(true)}>PDF-Vorschau</button>
              <button className="me-btn" style={modalPrimary} disabled={schreibenModal.busy} onClick={() => schreibenPdf(false)}>
                {schreibenModal.busy ? "Erzeugt…" : "PDF erzeugen + ablegen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {themenOffen && <ThemenVerwaltung onClose={() => setThemenOffen(false)} />}

      {firmenwechsel && (
        <FirmenwechselAssistent key={String(firmenwechsel)} vorgangId={typeof firmenwechsel === "number" ? firmenwechsel : null}
          vollbildStart={vollbild && !!initialFirmenwechsel}
          onClose={() => { setFirmenwechsel(false); setSchreibenStand((x) => x + 1); }}
          onFertig={() => setSchreibenStand((x) => x + 1)} />
      )}
      {neuerMieter && (
        <NeuerMieterAssistent onClose={() => setNeuerMieter(false)}
          onFertig={async (erg) => {
            setNeuerMieter(false);
            await laden();
            setMeldung(`Neuer Mieter angelegt: ${erg.mietvertrag_nr}`
              + (erg.auszug_id ? " · Auszug des bisherigen Mieters verknüpft" : ""));
            if (erg.einzug_id) setOffenId(erg.einzug_id);
          }} />
      )}

      {protokoll && detail && (
        <UebergabeprotokollForm mietvertragNr={detail.mietvertrag_nr} art={protokoll.art} kuendigungId={detail.id}
          onClose={() => setProtokoll(null)}
          onErzeugt={() => { ladeDetail(detail.id, true); }} />
      )}

      {folgevertrag && (
        <FolgevertragModal einheit={folgevertrag.einheit} onClose={() => setFolgevertrag(null)} mitUebergabeTermin
          absenden={detail && detail.art !== "einzug" ? async (nutzlast, extra) => {
            // Welle 109: wie „Neuer Mieter" — Einzug anlegen und mit diesem Auszug verknüpfen
            const r = await fetch(`${API_BASE}/mietverwaltung/api/vorgang/neuer-mieter`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ einheit: folgevertrag.einheit, alter_vertrag: detail.mietvertrag_nr,
                neuer_vertrag: nutzlast, uebergabe_termin_einzug: extra?.uebergabeTermin || null }) });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.detail || "Anlegen fehlgeschlagen");
            return j;
          } : undefined}
          onCreated={async (neu) => {
            setFolgevertrag(null);
            if (detail) await ladeDetail(detail.id);
            laden();
            setMeldung(`Folgevertrag ${neu.mietvertrag_nr} angelegt und mit diesem Vorgang verknüpft.`);
          }} />
      )}
    </>
  );
}
